<script setup>
import { RouterLink, RouterView, useRoute } from 'vue-router';
import { ref, computed, onMounted, onUnmounted } from 'vue';

// Key on the first path segment so /showcase/* tab switches don't re-trigger
// the outer page transition — the inner sc-page transition handles those.

const route = useRoute();

const PAGE_INFO = {
  overview:   { eyebrow: 'Byte Gaming Arena',  label: 'Overview' },
  clients:    { eyebrow: 'CRM',                label: 'Clients' },
  restaurant: { eyebrow: 'Food & Beverage',    label: 'Restaurant' },
  pcs:        { eyebrow: 'Gaming Floor',       label: 'PC Fleet' },
  store:      { eyebrow: 'Retail',             label: 'Store' },
  config:     { eyebrow: 'System',             label: 'Configuration' },
  login:      { eyebrow: 'Auth',               label: 'Sign In' },
};

const routeKey = computed(() => route.path.split('/').filter(Boolean)[0] || 'home');

const pageInfo = computed(() => {
  if (route.path.startsWith('/showcase')) return { eyebrow: 'Developer', label: 'UI Showcase' };
  return PAGE_INFO[route.name] ?? { eyebrow: 'Byte Dashboard', label: String(route.name ?? '') };
});

const now = ref(new Date());
let timer;
onMounted(() => { timer = setInterval(() => { now.value = new Date(); }, 1000); });
onUnmounted(() => clearInterval(timer));

const currentTime = computed(() =>
  now.value.toLocaleTimeString('en-HN', { hour: '2-digit', minute: '2-digit', hour12: false }),
);
</script>

<template>
  <div class="app-shell">

    <!-- ── Floating sidebar ───────────────────────────────────────────── -->
    <aside class="sidebar">
      <!-- grain layer -->
      <div class="sidebar__grain" aria-hidden="true" />

      <!-- Logo mark -->
      <RouterLink to="/" class="brand" aria-label="Byte Dashboard home">
        <div class="brand-mark">BYTE</div>
        <div class="brand-divider" aria-hidden="true" />
        <span class="brand-sub">Gaming Arena Dashboard</span>
      </RouterLink>

      <!-- Primary nav -->
      <nav class="side-nav" aria-label="Main navigation">
        <RouterLink to="/"           class="nav-item" active-class="">Overview</RouterLink>
        <RouterLink to="/clients"    class="nav-item">Clients</RouterLink>
        <RouterLink to="/restaurant" class="nav-item">Restaurant</RouterLink>
        <RouterLink to="/pcs"        class="nav-item">PCs</RouterLink>
        <RouterLink to="/store"      class="nav-item">Store</RouterLink>
        <RouterLink to="/config"     class="nav-item">Config</RouterLink>
      </nav>

      <!-- Footer -->
      <div class="sidebar-footer">
        <RouterLink to="/showcase" class="nav-item nav-item--dev">UI Kit</RouterLink>
        <RouterLink to="/login"    class="nav-item nav-item--auth">Sign in</RouterLink>
        <div class="status-strip">
          <span class="status-dot" aria-hidden="true" />
          <span class="status-label">LIVE · TEGU-01</span>
        </div>
      </div>
    </aside>

    <!-- ── Content column ─────────────────────────────────────────────── -->
    <div class="content-col">

      <header class="topbar">
        <div class="topbar-left">
          <span class="topbar-eyebrow">{{ pageInfo.eyebrow }}</span>
          <span class="topbar-sep" aria-hidden="true">/</span>
          <span class="topbar-label">{{ pageInfo.label }}</span>
        </div>
        <div class="topbar-right">
          <span class="topbar-time">{{ currentTime }}</span>
        </div>
      </header>

      <main>
        <RouterView v-slot="{ Component }">
          <Transition name="page" mode="out-in">
            <component :is="Component" :key="routeKey" />
          </Transition>
        </RouterView>
      </main>

    </div>
  </div>
</template>

<style scoped>
/* ── Shell ───────────────────────────────────────────────────────────────── */
.app-shell {
  min-height: 100vh;
  display: grid;
  grid-template-columns: 252px 1fr;
}

/* ── Floating sidebar ────────────────────────────────────────────────────── */
.sidebar {
  position: sticky;
  top: var(--bc-space-4);
  height: calc(100vh - var(--bc-space-8));
  margin: var(--bc-space-4) 0 var(--bc-space-4) var(--bc-space-4);

  display: flex;
  flex-direction: column;

  /* Toast glass recipe */
  background: var(--bc-glass-bg-strong);
  backdrop-filter: var(--bc-glass-filter-strong);
  -webkit-backdrop-filter: var(--bc-glass-filter-strong);
  border: var(--bc-glass-border-strong);
  border-radius: var(--bc-radius-card-lg);
  box-shadow: var(--bc-shadow-lg);

  overflow: hidden;
  isolation: isolate;
}

/* Grain overlay — same recipe as Toast */
.sidebar__grain {
  position: absolute;
  inset: 0;
  background-image: var(--bc-noise-grain);
  mix-blend-mode: overlay;
  opacity: 0.3;
  pointer-events: none;
  z-index: 0;
  border-radius: inherit;
}

/* Everything inside stacks above the grain */
.brand,
.side-nav,
.sidebar-footer {
  position: relative;
  z-index: 1;
}

/* ── Logo mark ───────────────────────────────────────────────────────────── */
.brand {
  display: flex;
  flex-direction: column;
  gap: var(--bc-space-2);
  text-decoration: none;
  padding: var(--bc-space-5) var(--bc-space-5) 0;
}

.brand-mark {
  font-family: var(--bc-font-display);
  font-weight: 900;
  font-size: 2.6rem;
  letter-spacing: var(--bc-tracking-tighter);
  text-transform: uppercase;
  line-height: 1;
  color: var(--bc-neon-yellow);
}

/* Thin rule beneath the wordmark */
.brand-divider {
  height: 1px;
  background: var(--bc-border);
  width: 100%;
  margin-top: var(--bc-space-1);
}

.brand-sub {
  font-family: var(--bc-font-mono);
  font-size: var(--bc-text-xs);
  text-transform: uppercase;
  letter-spacing: var(--bc-tracking-widest);
  color: var(--bc-text-muted);
}

/* ── Navigation ──────────────────────────────────────────────────────────── */
.side-nav {
  display: flex;
  flex-direction: column;
  padding: var(--bc-space-5) var(--bc-space-3) var(--bc-space-4);
  flex: 1;
  gap: var(--bc-space-1);
}

.nav-item {
  display: block;
  font-family: var(--bc-font-ui);
  font-size: var(--bc-text-sm);
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: var(--bc-tracking-wide);
  text-decoration: none;
  color: var(--bc-text-muted);
  padding: var(--bc-space-2) var(--bc-space-3);
  border-radius: var(--bc-radius-sm);
  transition:
    color var(--bc-transition-fast),
    background var(--bc-transition-fast);
}

.nav-item:hover {
  color: var(--bc-text);
  background: oklch(100% 0.008 95 / 0.06);
}

/* Active — same color-mix tint wash the toasts use */
.nav-item.router-link-active,
.nav-item.router-link-exact-active {
  color: var(--bc-neon-yellow);
  background: color-mix(in srgb, var(--bc-neon-yellow) 13%, transparent);
}

.nav-item--dev {
  color: var(--bc-neon-violet) !important;
}
.nav-item--dev.router-link-active,
.nav-item--dev.router-link-exact-active {
  background: color-mix(in srgb, var(--bc-neon-violet) 13%, transparent);
}

.nav-item--auth {
  color: var(--bc-text-muted) !important;
}
.nav-item--auth:hover {
  color: var(--bc-text) !important;
}

/* ── Sidebar footer ──────────────────────────────────────────────────────── */
.sidebar-footer {
  padding: var(--bc-space-3) var(--bc-space-3) var(--bc-space-4);
  border-top: 1px solid var(--bc-border);
  display: flex;
  flex-direction: column;
  gap: var(--bc-space-1);
}

.status-strip {
  display: flex;
  align-items: center;
  gap: var(--bc-space-2);
  padding: var(--bc-space-3) var(--bc-space-3) 0;
}

.status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--bc-success);
  box-shadow: 0 0 6px var(--bc-success);
  flex-shrink: 0;
  animation: bc-status-pulse 3s ease-in-out infinite;
}

.status-label {
  font-family: var(--bc-font-mono);
  font-size: var(--bc-text-xs);
  color: var(--bc-text-muted);
  letter-spacing: var(--bc-tracking-wider);
}

@keyframes bc-status-pulse {
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.35; }
}

/* ── Content column ──────────────────────────────────────────────────────── */
.content-col {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  overflow: hidden;
}

/* ── Topbar ──────────────────────────────────────────────────────────────── */
.topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 52px;
  padding: 0 var(--bc-space-8);
  border-bottom: 1px solid var(--bc-border);
  position: sticky;
  top: 0;
  z-index: var(--bc-z-sticky);
  flex-shrink: 0;
  background: oklch(0% 0 0 / 0.6);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}

.topbar-left {
  display: flex;
  align-items: center;
  gap: var(--bc-space-3);
}

.topbar-eyebrow {
  font-family: var(--bc-font-mono);
  font-size: var(--bc-text-xs);
  text-transform: uppercase;
  letter-spacing: var(--bc-tracking-wider);
  color: var(--bc-text-muted);
}

.topbar-sep {
  font-family: var(--bc-font-mono);
  font-size: var(--bc-text-xs);
  color: var(--bc-text-muted);
  opacity: 0.4;
}

.topbar-label {
  font-family: var(--bc-font-ui);
  font-size: var(--bc-text-sm);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: var(--bc-tracking-wide);
  color: var(--bc-text);
}

.topbar-time {
  font-family: var(--bc-font-mono);
  font-size: var(--bc-text-sm);
  color: var(--bc-text-muted);
  letter-spacing: 0.05em;
}

/* ── Main content ────────────────────────────────────────────────────────── */
main {
  flex: 1;
  padding: var(--bc-space-10) var(--bc-space-8);
  max-width: 1280px;
  width: 100%;
  box-sizing: border-box;
}
</style>
